import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../utils/api';
import { jwtDecode } from 'jwt-decode';

// Define roles
export const ROLES = {
  BUYER: 'buyer',
  INVENTORY_MANAGER: 'inventory_manager',
  ADMINISTRATOR: 'administrator',
  SUPER_USER: 'super_user',
  CASHIER: 'cashier',
};

const AuthContext = createContext();

const normalizeErrorMessage = (payload, fallbackMessage) => {
  if (typeof payload === 'string') {
    return payload;
  }
  if (Array.isArray(payload)) {
    const messages = payload
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        if (entry && typeof entry.msg === 'string') return entry.msg;
        return null;
      })
      .filter(Boolean);
    return messages.length > 0 ? messages.join(', ') : fallbackMessage;
  }
  if (payload && typeof payload === 'object') {
    if (typeof payload.msg === 'string') return payload.msg;
    if (typeof payload.message === 'string') return payload.message;
  }
  return fallbackMessage;
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const hasStoredAccessToken = () => Boolean(localStorage.getItem('accessToken'));

  const redirectToLogin = (reason) => {
    if (reason) {
      sessionStorage.setItem('authRedirectReason', reason);
    }
    // Avoid forcing a full page reload; ProtectedRoute will navigate to /login
    // after auth state is cleared.
  };

  // Initialize auth state from localStorage
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const loadUser = async () => {
      if (token) {
        try {
          const decoded = jwtDecode(token);
          let userData = { ...decoded, token };

          if (!decoded?.name) {
            try {
              const response = await axios.get('/api/auth/me');
              userData = { ...userData, ...response.data };
            } catch (fetchError) {
              console.warn('Could not fetch user profile for name fallback', fetchError);
            }
          }

          setUser(userData);
        } catch (error) {
          console.error('Invalid token:', error);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  // Keep in-memory auth state aligned with token presence.
  useEffect(() => {
    const syncAuthState = () => {
      if (!hasStoredAccessToken()) {
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncAuthState();
      }
    };

    window.addEventListener('storage', syncAuthState);
    window.addEventListener('focus', syncAuthState);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', syncAuthState);
      window.removeEventListener('focus', syncAuthState);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const refreshCurrentUser = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setUser(null);
      return null;
    }

    const decoded = jwtDecode(token);
    const me = await axios.get('/api/auth/me');
    const mergedUser = { ...decoded, ...me.data, token };
    setUser(mergedUser);
    return mergedUser;
  };

  // Axios interceptor for token refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const requestUrl = originalRequest?.url || '';
        const isRefreshRequest = requestUrl.includes('/api/auth/refresh');

        if (error.response?.status === 401 && isRefreshRequest) {
          logout();
          redirectToLogin('session-expired');
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest?._retry) {
          originalRequest._retry = true;
          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
              logout();
              redirectToLogin('session-expired');
              return Promise.reject(error);
            }

            const response = await axios.post('/api/auth/refresh', { refreshToken });
            const { accessToken } = response.data;
            localStorage.setItem('accessToken', accessToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
            originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            logout();
            redirectToLogin('session-expired');
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const handleLoginSuccess = async (accessToken, refreshToken) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

    const decoded = jwtDecode(accessToken);
    let userData = { ...decoded, token: accessToken };
    try {
      const me = await axios.get('/api/auth/me');
      userData = { ...userData, ...me.data };
    } catch (meError) {
      console.warn('Could not fetch /api/auth/me after login', meError);
    }

    const loginRoles = Array.isArray(userData.roles) && userData.roles.length > 0 ? userData.roles : (userData.role ? [userData.role] : []);
    if (loginRoles.includes(ROLES.BUYER)) {
      try {
        const verificationRes = await axios.get('/api/email-verification/status');
        userData = {
          ...userData,
          email_verified: Boolean(verificationRes.data?.email_verified),
        };
      } catch (verificationErr) {
        console.warn('Could not fetch email verification status after login', verificationErr);
      }
    }

    setUser(userData);
    return { success: true, user: userData };
  };

  const loginWithUserIdPassword = async (userId, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        user_id: userId,
        password,
      });
      const { accessToken, refreshToken } = response.data;
      return await handleLoginSuccess(accessToken, refreshToken);
    } catch (error) {
      const responseData = error.response?.data;
      const detail = responseData?.detail;
      const normalizedMessage = normalizeErrorMessage(detail ?? responseData?.message, 'Login failed');
      const statusCode = error.response?.status;
      const message = normalizedMessage.includes('Account suspended. Send mail to')
        ? normalizedMessage
        : (statusCode === 401 || statusCode === 422
          ? 'Incorrect credentials. Login failed'
          : normalizedMessage);
      return { success: false, message };
    }
  };

  const loginWithEmailPassword = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        email,
        password,
      });
      const { accessToken, refreshToken } = response.data;
      return await handleLoginSuccess(accessToken, refreshToken);
    } catch (error) {
      const responseData = error.response?.data;
      const detail = responseData?.detail;
      const normalizedMessage = normalizeErrorMessage(detail ?? responseData?.message, 'Login failed');
      const statusCode = error.response?.status;
      const message = normalizedMessage.includes('Account suspended. Send mail to')
        ? normalizedMessage
        : (statusCode === 401 || statusCode === 422
          ? 'Incorrect credentials. Login failed'
          : normalizedMessage);
      return { success: false, message };
    }
  };

  const requestPhoneLoginOtp = async (countryIsdCode, phoneNumber) => {
    try {
      await axios.post('/api/auth/login/phone/send-otp', {
        country_isd_code: countryIsdCode,
        phone_number: phoneNumber,
      });
      return { success: true, message: 'OTP sent to WhatsApp' };
    } catch (error) {
      const responseData = error.response?.data;
      const detail = responseData?.detail;
      return {
        success: false,
        message: normalizeErrorMessage(detail ?? responseData?.message, 'Failed to send OTP'),
      };
    }
  };

  const verifyPhoneLoginOtp = async (countryIsdCode, phoneNumber, otp) => {
    try {
      const response = await axios.post('/api/auth/login/phone/verify-otp', {
        country_isd_code: countryIsdCode,
        phone_number: phoneNumber,
        otp,
      });
      const { accessToken, refreshToken } = response.data;
      return await handleLoginSuccess(accessToken, refreshToken);
    } catch (error) {
      const responseData = error.response?.data;
      const detail = responseData?.detail;
      return {
        success: false,
        message: normalizeErrorMessage(detail ?? responseData?.message, 'OTP verification failed'),
      };
    }
  };

  const login = async (email, password) => {
    return loginWithEmailPassword(email, password);
  };

  const checkUserIdAvailability = async (userId) => {
    try {
      const response = await axios.get('/api/auth/user-id-availability', {
        params: { user_id: userId },
      });
      return { success: true, ...response.data };
    } catch (error) {
      const responseData = error.response?.data;
      const detail = responseData?.detail;
      return {
        success: false,
        message: normalizeErrorMessage(detail ?? responseData?.message, 'Unable to check availability'),
      };
    }
  };

  const register = async ({ userId, firstName, lastName, email, password, countryIsdCode, phoneNumber }) => {
    try {
      await axios.post('/api/auth/register', {
        user_id: userId,
        first_name: firstName,
        last_name: lastName,
        email: email || null,
        password,
        country_isd_code: countryIsdCode,
        phone_number: phoneNumber,
      });
      return { success: true, message: 'Registration successful' };
    } catch (error) {
      const responseData = error.response?.data;
      const detail = responseData?.detail;
      if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        return { success: false, message: detail };
      }
      const message = normalizeErrorMessage(detail ?? responseData?.message, 'Registration failed');
      return { success: false, message };
    }
  };

  const forgotUserIdByEmail = async (email) => {
    try {
      await axios.post('/api/auth/forgot-user-id/email', { email });
      return { success: true, message: 'User ID sent to your email' };
    } catch (error) {
      const responseData = error.response?.data;
      const detail = responseData?.detail;
      return {
        success: false,
        message: normalizeErrorMessage(detail ?? responseData?.message, 'Failed to send user ID email'),
      };
    }
  };

  const forgotUserIdSendPhoneOtp = async (countryIsdCode, phoneNumber) => {
    try {
      await axios.post('/api/auth/forgot-user-id/phone/send-otp', {
        country_isd_code: countryIsdCode,
        phone_number: phoneNumber,
      });
      return { success: true, message: 'OTP sent to WhatsApp' };
    } catch (error) {
      const responseData = error.response?.data;
      const detail = responseData?.detail;
      return {
        success: false,
        message: normalizeErrorMessage(detail ?? responseData?.message, 'Failed to send OTP'),
      };
    }
  };

  const forgotUserIdVerifyPhoneOtp = async (countryIsdCode, phoneNumber, otp) => {
    try {
      const response = await axios.post('/api/auth/forgot-user-id/phone/verify-otp', {
        country_isd_code: countryIsdCode,
        phone_number: phoneNumber,
        otp,
      });
      return { success: true, userId: response.data?.user_id };
    } catch (error) {
      const responseData = error.response?.data;
      const detail = responseData?.detail;
      return {
        success: false,
        message: normalizeErrorMessage(detail ?? responseData?.message, 'OTP verification failed'),
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post('/api/auth/forgot-password', { email });
      return { success: true, message: 'Password reset email sent' };
    } catch (error) {
      const responseData = error.response?.data;
      const detail = responseData?.detail;
      const message = normalizeErrorMessage(detail ?? responseData?.message, 'Failed to send reset email');
      return { success: false, message };
    }
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const hasRole = (role) => {
    if (!hasStoredAccessToken() || !user) return false;
    // Use roles array from JWT when available, fall back to single role field.
    const userRoles = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles : (user.role ? [user.role] : []);
    return userRoles.some((userRole) => String(userRole).toLowerCase() === String(role).toLowerCase());
  };

  const hasAnyRole = (roles) => {
    if (!hasStoredAccessToken() || !user) return false;
    const userRoles = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles : (user.role ? [user.role] : []);
    return roles.some((role) => userRoles.some((userRole) => String(userRole).toLowerCase() === String(role).toLowerCase()));
  };

  const isAuthenticated = () => {
    return hasStoredAccessToken() && !!user;
  };

  const value = {
    user,
    login,
    loginWithUserIdPassword,
    loginWithEmailPassword,
    requestPhoneLoginOtp,
    verifyPhoneLoginOtp,
    register,
    checkUserIdAvailability,
    refreshCurrentUser,
    forgotPassword,
    forgotUserIdByEmail,
    forgotUserIdSendPhoneOtp,
    forgotUserIdVerifyPhoneOtp,
    logout,
    hasRole,
    hasAnyRole,
    isAuthenticated,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};